import { GraphQLCameraMediaRequester } from '@cobliteam/api-lib';
import { type EventFilters, type Media, type MediaFilters, type MediaRequest, MediaRequestStatusFilterV2, MediaType, type RequestFilters } from '@cobliteam/common-types';
import { addDays, subDays } from '@cobliteam/utils/date';
import { type DefinedInitialDataInfiniteOptions, type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';

// Lazy instantiation to avoid circular dependency issues
const getCameraMediaGraphQLRequester = () => new GraphQLCameraMediaRequester();

type BidirectionPageParam = {
  page: number;
  totalElements: number;
  afterPages: number;
  afterCount: number;
  beforePages: number;
  beforeCount: number;
};

type BidirectionPageType = {
  content: Media[];
} & BidirectionPageParam;

const createPageParam = (page: BidirectionPageType): BidirectionPageParam => ({
  afterCount: page.afterCount,
  afterPages: page.afterPages,
  beforePages: page.beforePages,
  beforeCount: page.beforeCount,
  totalElements: page.totalElements,
  page: 0,
});

type AnyFilter = EventFilters & RequestFilters;

const createDateFilters = (timestamp: number, filters: MediaFilters & RequestFilters, isEvents: boolean): [Partial<AnyFilter>, Partial<AnyFilter>] => {
  const sortKey = isEvents ? 'startTime' : 'createdAt';
  const rangeKey = isEvents ? 'mediaPeriod' : 'requestedAt';
  const existingRange = filters[rangeKey];

  let from: number | undefined = existingRange ? existingRange.start : subDays(timestamp, 3).getTime();
  let to: number | undefined = existingRange ? existingRange.end : addDays(timestamp, 3).getTime();

  // Favorite medias are not constrained with ranges
  if (filters.favoriteMedia) {
    from = undefined;
    to = undefined;
  }

  return [
    {
      [rangeKey]: {
        start: from,
        end: timestamp - 1,
      },
      sort: { column: sortKey, order: 'desc' },
    },
    {
      [rangeKey]: {
        start: timestamp + 1,
        end: to,
      },
      sort: { column: sortKey, order: 'asc' },
    },
  ];
};

const useBidirectionalMedias = (
  media: Media,
  filters: EventFilters,
  options?: Partial<DefinedInitialDataInfiniteOptions<BidirectionPageType, unknown, InfiniteData<BidirectionPageType>, unknown[], BidirectionPageParam>>
) => {
  const isEvent = media?.mediaType === MediaType.EVENT_VIDEO;

  const key = isEvent ? 'BIDIRECTION-EVENTS' : 'BIDIRECTION-REQUESTS';
  const query = isEvent ? getCameraMediaGraphQLRequester().getMediaEventList : getCameraMediaGraphQLRequester().getMediaRequestList;
  const defaultMediaTypes = isEvent ? [MediaType.EVENT_VIDEO] : [MediaType.FOOTAGE_VIDEO, MediaType.TIMELAPSE];
  const mediaTypes = Array.isArray(filters.mediaTypes) && filters.mediaTypes.length > 0 ? filters.mediaTypes : defaultMediaTypes;
  const requestStatus = isEvent ? [] : [MediaRequestStatusFilterV2.COMPLETED];

  const timestamp = isEvent ? media?.timestamp : (media?.mediaDetails as MediaRequest)?.createdAt;

  return useInfiniteQuery<BidirectionPageType, unknown, InfiniteData<BidirectionPageType>, unknown[], BidirectionPageParam>({
    queryKey: ['CAMERA-MEDIA', key, filters],
    initialPageParam: {
      page: 0,
      totalElements: 0,
      afterCount: 0,
      afterPages: 0,
      beforeCount: 0,
      beforePages: 0,
    },
    queryFn: async ({ pageParam }) => {
      const { page, ...otherPageParams } = pageParam;

      const [beforeDateFilter, afterDateFilter] = createDateFilters(timestamp, filters, isEvent);

      // One goes down
      const beforeMedias =
        page >= 0
          ? await query({
              ...filters,
              ...beforeDateFilter,
              requestStatus,
              page,
              size: 5,
              mediaTypes,
            })
          : null;

      const { content: beforeContent = [], totalElements: beforeCount = 0, totalPages: beforePages = 0 } = beforeMedias || {};
      // Include preloaded media at the begin
      const beforeContentParsed = page === 0 ? [media, ...beforeContent] : beforeContent;

      // Other goes up
      const afterPage = page === 0 ? 0 : -1 * page;
      const afterMedias =
        page <= 0
          ? await query({
              ...filters,
              ...afterDateFilter,
              requestStatus,
              page: afterPage,
              size: 5,
              mediaTypes,
            })
          : null;

      const { content: afterContent = [], totalElements: afterCount = 0, totalPages: afterPages = 0 } = afterMedias || {};

      const content = afterContent.toReversed().concat(beforeContentParsed);

      return {
        content,
        page: pageParam.page,
        ...(page !== 0
          ? otherPageParams
          : {
              afterCount,
              afterPages,
              beforeCount,
              beforePages,
              totalElements: afterCount + beforeCount + 1,
            }),
      };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.page + 1 >= lastPage.beforePages) return;

      return {
        ...createPageParam(lastPage),
        page: lastPage.page + 1,
      };
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.page - 1 <= -1 * firstPage.afterPages) return;

      return {
        ...createPageParam(firstPage),
        page: firstPage.page - 1,
      };
    },
    ...options,
  });
};

export default useBidirectionalMedias;
